function clonePoint(point) {
  return { x: Number(point.x), y: Number(point.y) };
}

export function quadCentroid(points) {
  if (!points || points.length === 0) return { x: 0, y: 0 };
  const sum = points.reduce((acc, point) => ({
    x: acc.x + point.x,
    y: acc.y + point.y,
  }), { x: 0, y: 0 });
  return {
    x: sum.x / points.length,
    y: sum.y / points.length,
  };
}

export function orderQuadPoints(points) {
  const centroid = quadCentroid(points);
  return points
    .map(clonePoint)
    .sort((a, b) => Math.atan2(a.y - centroid.y, a.x - centroid.x) - Math.atan2(b.y - centroid.y, b.x - centroid.x));
}

export function rotateQuad(points, offset = 0) {
  const ordered = orderQuadPoints(points);
  if (ordered.length === 0) return [];
  const normalizedOffset = ((offset % ordered.length) + ordered.length) % ordered.length;
  return ordered.map((_, index) => ordered[(index + normalizedOffset) % ordered.length]);
}

/** Axis-aligned bounds of points: { minX, minY, maxX, maxY }. */
export function quadBounds(points) {
  let minX = Infinity; let minY = Infinity; let maxX = -Infinity; let maxY = -Infinity;
  for (const p of points) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }
  return { minX, minY, maxX, maxY };
}

export function distanceBetweenPoints(a, b) {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return Math.sqrt(dx * dx + dy * dy);
}

export function averageCornerDistance(aPoints, bPoints) {
  if (!aPoints || !bPoints || aPoints.length !== bPoints.length || aPoints.length === 0) {
    return Infinity;
  }

  let best = Infinity;
  const orderedA = orderQuadPoints(aPoints);
  const orderedB = orderQuadPoints(bPoints);
  for (let offset = 0; offset < orderedB.length; offset++) {
    const rotated = rotateQuad(orderedB, offset);
    const total = orderedA.reduce((sum, point, index) => sum + distanceBetweenPoints(point, rotated[index]), 0);
    best = Math.min(best, total / orderedA.length);
  }
  return best;
}

export function polygonArea(points) {
  if (!points || points.length < 3) return 0;
  let area = 0;
  for (let i = 0; i < points.length; i++) {
    const current = points[i];
    const next = points[(i + 1) % points.length];
    area += current.x * next.y - next.x * current.y;
  }
  return Math.abs(area) / 2;
}

function isInside(point, edgeStart, edgeEnd) {
  return ((edgeEnd.x - edgeStart.x) * (point.y - edgeStart.y)) - ((edgeEnd.y - edgeStart.y) * (point.x - edgeStart.x)) >= 0;
}

function lineIntersection(a1, a2, b1, b2) {
  const denominator = ((a1.x - a2.x) * (b1.y - b2.y)) - ((a1.y - a2.y) * (b1.x - b2.x));
  if (Math.abs(denominator) < 1e-6) {
    return clonePoint(a2);
  }

  const determinantA = a1.x * a2.y - a1.y * a2.x;
  const determinantB = b1.x * b2.y - b1.y * b2.x;
  return {
    x: ((determinantA * (b1.x - b2.x)) - ((a1.x - a2.x) * determinantB)) / denominator,
    y: ((determinantA * (b1.y - b2.y)) - ((a1.y - a2.y) * determinantB)) / denominator,
  };
}

export function clipPolygon(subjectPoints, clipPoints) {
  let output = orderQuadPoints(subjectPoints);
  const clip = orderQuadPoints(clipPoints);
  if (output.length === 0 || clip.length === 0) return [];

  for (let i = 0; i < clip.length; i++) {
    const edgeStart = clip[i];
    const edgeEnd = clip[(i + 1) % clip.length];
    const input = output;
    output = [];
    if (input.length === 0) break;

    let previous = input[input.length - 1];
    for (const current of input) {
      const currentInside = isInside(current, edgeStart, edgeEnd);
      const previousInside = isInside(previous, edgeStart, edgeEnd);

      if (currentInside) {
        if (!previousInside) {
          output.push(lineIntersection(previous, current, edgeStart, edgeEnd));
        }
        output.push(clonePoint(current));
      } else if (previousInside) {
        output.push(lineIntersection(previous, current, edgeStart, edgeEnd));
      }
      previous = current;
    }
  }

  return output;
}

export function polygonIoU(aPoints, bPoints) {
  const intersection = clipPolygon(aPoints, bPoints);
  const intersectionArea = polygonArea(intersection);
  const unionArea = polygonArea(aPoints) + polygonArea(bPoints) - intersectionArea;
  if (unionArea <= 0) return 0;
  return intersectionArea / unionArea;
}

export function quadFromCandidate(candidate) {
  if (!candidate) return [];

  if (candidate.quad?.length === 4) {
    return orderQuadPoints(candidate.quad);
  }

  const angle = candidate.obbAngle || 0;
  const centerX = candidate.obbCx ?? (candidate.x + candidate.w / 2);
  const centerY = candidate.obbCy ?? (candidate.y + candidate.h / 2);
  const width = candidate.obbW || candidate.w;
  const height = candidate.obbH || candidate.h;

  const halfWidth = width / 2;
  const halfHeight = height / 2;
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  const corners = [
    { x: -halfWidth, y: -halfHeight },
    { x: halfWidth, y: -halfHeight },
    { x: halfWidth, y: halfHeight },
    { x: -halfWidth, y: halfHeight },
  ].map((point) => ({
    x: centerX + point.x * cos - point.y * sin,
    y: centerY + point.x * sin + point.y * cos,
  }));

  return orderQuadPoints(corners);
}

export function pointInPolygon(point, polygon) {
  if (!polygon || polygon.length < 3) return false;

  let sign = 0;
  for (let i = 0; i < polygon.length; i++) {
    const current = polygon[i];
    const next = polygon[(i + 1) % polygon.length];
    const cross = ((next.x - current.x) * (point.y - current.y)) - ((next.y - current.y) * (point.x - current.x));
    if (Math.abs(cross) < 1e-6) continue;
    const nextSign = Math.sign(cross);
    if (sign === 0) {
      sign = nextSign;
    } else if (nextSign !== sign) {
      return false;
    }
  }
  return true;
}

export function pointInCandidate(point, candidate, slack = 0) {
  const quad = quadFromCandidate(candidate);
  if (quad.length !== 4) return false;

  if (slack <= 0) return pointInPolygon(point, quad);

  const centroid = quadCentroid(quad);
  const expanded = quad.map((corner) => {
    const dx = corner.x - centroid.x;
    const dy = corner.y - centroid.y;
    const length = Math.max(Math.sqrt(dx * dx + dy * dy), 1);
    return {
      x: corner.x + (dx / length) * slack,
      y: corner.y + (dy / length) * slack,
    };
  });
  return pointInPolygon(point, expanded);
}
